import { FoodItem, FoodItemTag, FoodTag } from "client/jspPlatformExperiment";
import {
  filterVariantIdentityTags,
  filterVariantTypeTags,
  variantTypeTagLabel,
  VariantTypeLabelLanguage,
} from "utils/food/foodTag";

/**
 * Build a variant's display name from the main name and its variant-type tags.
 * Tag names are sorted alphabetically so the result is deterministic regardless
 * of selection order (a variant's identity is an unordered set of tags). With
 * no variant-type tags the bare main name is returned, so callers can fall back
 * to the stored name for legacy / not-yet-typed variants.
 *
 * Tags carry a name in each language (`tag_name` is French, `tag_description`
 * is English) and the `language` parameter selects which, so the same util
 * drives both the English food_name and the French recipe.name from a single
 * tag set. Labels go through `variantTypeTagLabel`, so variant-type display
 * overrides (e.g. "Lactose free" / "Sans Lactose") apply here too.
 */
export const deriveVariantName = (
  mainName: string | null | undefined,
  variantTypeTags: (FoodItemTag | FoodTag)[],
  language: VariantTypeLabelLanguage = "fr",
): string => {
  const base = (mainName ?? "").trim();
  const tagNames = variantTypeTags
    .map((tag) => variantTypeTagLabel(tag, language))
    .filter((name): name is string => !!name)
    .sort((a, b) => a.localeCompare(b));
  if (tagNames.length === 0) {
    return base;
  }
  if (!base) {
    return `(${tagNames.join(", ")})`;
  }
  return `${base} (${tagNames.join(", ")})`;
};

/**
 * Search-results variant of FoodItem that may carry nested variants under
 * `variant_food_items`, mirroring the FoodItemAndVariantFoods response shape.
 * Until openapi-ts regenerates against the updated backend, we widen the
 * known FoodItem type locally instead of changing global types.
 */
export type FoodItemWithVariants = FoodItem & {
  variant_food_items?: FoodItem[];
};

/**
 * A food item is a variant when it points at an original food via
 * `original_food_id`. Self-references are treated as mains, not variants.
 */
export const isFoodVariant = (food: FoodItem): boolean =>
  food.original_food_id != null && food.original_food_id !== food.id;

/**
 * Return the variants attached to a given main food id. Reads from the
 * `variant_food_items` field that the search endpoint embeds on each main.
 */
export const getVariantsOfMain = (
  foodItemOptions: FoodItem[],
  mainFoodId: number,
): FoodItem[] => {
  const main = foodItemOptions.find((f) => f.id === mainFoodId) as
    | FoodItemWithVariants
    | undefined;
  return main?.variant_food_items ?? [];
};

/**
 * Build a flat lookup of every loaded food keyed by id — including the
 * variants nested under each main's `variant_food_items`. Use this when the
 * caller has a food_id and needs the corresponding FoodItem (e.g. to render
 * a variant's display name or to inspect its `original_food_id`).
 */
export const buildFlatFoodLookup = (
  foodItemOptions: FoodItem[],
): Map<number, FoodItem> => {
  const map = new Map<number, FoodItem>();
  foodItemOptions.forEach((main) => {
    map.set(main.id, main);
    const variants = (main as FoodItemWithVariants).variant_food_items;
    variants?.forEach((variant) => {
      map.set(variant.id, variant);
    });
  });
  return map;
};

/**
 * Short label identifying a variant by the tags that define it (e.g. "Spicy"
 * or "Large, Spicy") rather than its full food name. Reads the food item tags
 * flagged is_variant_identity by the backend, falling back to variant-type
 * category tags for foods saved before the flag existed. Tag labels are
 * sorted for deterministic output. Prefers the English label (via
 * `variantTypeTagLabel`, so display overrides apply), falling back to the
 * French `tag_name` per tag, and to the food name when a food has no
 * identifying tags at all (legacy / untyped).
 */
export const getVariantTypeLabel = (food: FoodItem): string => {
  const identityTags = filterVariantIdentityTags(food.food_tags ?? []);
  const identifyingTags =
    identityTags.length > 0
      ? identityTags
      : filterVariantTypeTags(food.food_tags ?? []);
  const tagLabels = identifyingTags
    .map((tag) => variantTypeTagLabel(tag, "en") ?? tag.tag_name)
    .filter((label): label is string => !!label)
    .sort((a, b) => a.localeCompare(b));
  if (tagLabels.length === 0) {
    return food.food_name ?? `Variant ${food.id}`;
  }
  return tagLabels.join(", ");
};
